import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export function useBillArchives(workspaceId?: string) {
  return useQuery({
    queryKey: ['bill-archives', workspaceId],
    queryFn: async () => {
      let query = supabase
        .from('bill_archives')
        .select('*');

      const { data, error } = await query.order('archived_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}

export function useRetentionPolicies(workspaceId: string) {
  return useQuery({
    queryKey: ['retention-policies', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('retention_policies')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useArchiveBill() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ billId, reason }: { billId: string; reason?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase.functions.invoke('archive-manager', {
        body: {
          action: 'archive',
          billId,
          workspaceId: currentWorkspace?.id,
          userId: user?.id,
          reason
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bill-archives'] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast.success(`Bill archived (${data.retentionYears} year retention)`);
    },
    onError: (error: Error) => {
      toast.error('Archive failed: ' + error.message);
    }
  });
}

export function useRestoreRequests() {
  return useQuery({
    queryKey: ['archive-restore-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('archive_restore_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}

export function useRequestRestore() {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ billId, reason }: { billId: string; reason: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase.functions.invoke('archive-manager', {
        body: {
          action: 'restore',
          billId,
          workspaceId: currentWorkspace?.id,
          userId: user?.id,
          reason
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive-restore-requests'] });
      toast.success('Restore request submitted');
    },
    onError: (error: Error) => {
      toast.error('Restore request failed: ' + error.message);
    }
  });
}

export function useCreateRetentionPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policy: any) => {
      const { data, error } = await supabase
        .from('retention_policies')
        .insert(policy)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retention-policies'] });
      toast.success('Retention policy created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create policy: ' + error.message);
    }
  });
}

export function useToggleLegalHold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ archiveId, legalHold, reason }: { archiveId: string; legalHold: boolean; reason?: string }) => {
      const { data, error } = await supabase
        .from('bill_archives')
        .update({ 
          legal_hold: legalHold,
          archive_reason: reason || undefined
        })
        .eq('id', archiveId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-archives'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to update legal hold: ' + error.message);
    }
  });
}
